import React, { useEffect, useState } from 'react';
import VisitaMapModal from './VisitaMapModal';
import { api, formatCOP, imageUrl } from '../services/api';
import { pedidoClienteLabel } from '../utils/textEncoding';
import { formatColombia } from '../utils/colombiaTime';

const PAGO_LABEL = {
  contado: 'Contado',
  credito_15: 'Crédito 15 días',
  credito_20: 'Crédito 20 días',
  credito_30: 'Facturación a 30 días',
};

function labelPago(v) {
  if (!v) return '—';
  return PAGO_LABEL[v] || String(v).replace(/_/g, ' ');
}

function labelTipoDoc(v) {
  const t = String(v || '').trim().toLowerCase();
  if (t === 'iva') return 'Factura';
  if (t === 'remision') return 'Remisión';
  return '—';
}

function labelDocCampo(v) {
  return String(v || '').trim().toLowerCase() === 'iva' ? 'Número de factura' : 'Código de remisión';
}

function labelFactEstado(v) {
  const st = String(v || '').trim().toLowerCase();
  if (st === 'en_proceso') return 'Facturando';
  if (st === 'facturado') return 'Facturado';
  return 'Por facturar';
}

const ESTADO = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_preparacion: 'En preparación',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

function fmtFecha(iso) {
  if (!iso) return '—';
  return formatColombia(iso, { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtDuracionVisita(inicio, fin) {
  if (!inicio) return null;
  const a = new Date(inicio).getTime();
  const b = fin ? new Date(fin).getTime() : Date.now();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  const mins = Math.round((b - a) / 60000);
  if (mins < 1) return 'Menos de 1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Duración legible entre dos fechas (o null si no aplica). */
function fmtDelta(desde, hasta) {
  if (!desde || !hasta) return null;
  const a = new Date(desde).getTime();
  const b = new Date(hasta).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null;
  const seg = Math.round((b - a) / 1000);
  if (seg < 60) return `${seg} s`;
  const mins = Math.round(seg / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Calcula los tiempos de cada etapa del pedido a partir de las marcas de tiempo. */
function calcularTiempos(pedido) {
  if (!pedido) return null;
  const log = Array.isArray(pedido.estado_log) ? pedido.estado_log : [];
  const primeraFecha = (estado) => {
    const ev = log.find((l) => l.estado_nuevo === estado);
    return ev?.created_at || null;
  };

  const creado = pedido.created_at;
  const confirmadoAt = primeraFecha('confirmado');
  const prepAt = pedido.preparado_at || primeraFecha('en_preparacion');
  const entregadoAt = pedido.revisado_at || primeraFecha('entregado') || (pedido.estado === 'entregado' ? pedido.preparado_at : null);
  const factInicio = pedido.facturado_inicio_at;
  const factFin = pedido.facturado_fin_at;
  const canceladoAt = primeraFecha('cancelado');

  const etapas = [
    { label: 'Esperó aprobación (oficina)', valor: fmtDelta(creado, confirmadoAt) },
    { label: 'Esperó en cola de bodega', valor: fmtDelta(confirmadoAt, prepAt) },
    { label: 'Separación en bodega', valor: fmtDelta(prepAt, entregadoAt) },
    { label: 'Facturación', valor: fmtDelta(factInicio, factFin) },
  ].filter((e) => e.valor);

  let total = null;
  if (entregadoAt) total = fmtDelta(creado, entregadoAt);
  else if (canceladoAt) total = fmtDelta(creado, canceladoAt);

  return { etapas, total, entregado: Boolean(entregadoAt), cancelado: Boolean(canceladoAt) };
}

function parsePuntosVisita(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function visitaMapLinks(pedido) {
  const v = pedido.visita || {};
  const puntos = parsePuntosVisita(v.puntos_gps);
  const valid = puntos.filter((p) => p.lat != null && p.lng != null);
  let lat = null;
  let lng = null;
  if (valid.length) {
    lat = valid[valid.length - 1].lat;
    lng = valid[valid.length - 1].lng;
  } else if (v.lat_fin != null && v.lng_fin != null) {
    lat = v.lat_fin;
    lng = v.lng_fin;
  } else if (v.lat_inicio != null && v.lng_inicio != null) {
    lat = v.lat_inicio;
    lng = v.lng_inicio;
  }
  if (lat == null || lng == null) return null;
  const mapa = `https://www.google.com/maps?q=${lat},${lng}`;
  let ruta = null;
  if (valid.length >= 2) {
    const origin = `${valid[0].lat},${valid[0].lng}`;
    const dest = `${valid[valid.length - 1].lat},${valid[valid.length - 1].lng}`;
    ruta = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
  }
  return { mapa, ruta, puntos: valid.length || 1 };
}

export default function AdminPedidoDetail({ id, pin, onClose }) {
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMapa, setShowMapa] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getAdminPedido(id);
        if (!cancelled) setPedido(res.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, pin]);

  return (
    <div className="pedido-detail-backdrop" onClick={onClose} role="presentation">
      <div className="pedido-detail-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="pedido-detail-head">
          <div>
            <h3>{pedido?.numero || 'Pedido'}</h3>
            {pedido ? (
              <span className={`estado-pill estado-${pedido.estado}`}>
                {ESTADO[pedido.estado] || pedido.estado}
              </span>
            ) : null}
          </div>
          <button type="button" className="pedido-detail-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        {loading ? <p className="muted">Cargando...</p> : null}
        {error ? <p className="error banner">{error}</p> : null}

        {pedido ? (
          <div className="pedido-detail-body">
            <div className="pedido-detail-grid">
              <p><span>Cliente</span><strong>{pedidoClienteLabel(pedido)}</strong></p>
              {pedido.es_sede || pedido.padre_codigo ? (
                <p>
                  <span>Entrega</span>
                  <strong>
                    <span className="cliente-sede-pill">Sucursal</span>
                    {pedido.padre_codigo ? ` · ${pedido.padre_codigo}` : ''}
                    {pedido.sede_etiqueta ? ` · ${pedido.sede_etiqueta}` : ''}
                  </strong>
                </p>
              ) : null}
              {pedido.sucursal_direccion || pedido.ciudad || pedido.zona ? (
                <p>
                  <span>Dirección</span>
                  <strong>
                    {pedido.sucursal_direccion || '—'}
                    {pedido.ciudad || pedido.zona
                      ? ` · ${[pedido.ciudad, pedido.zona].filter(Boolean).join(' · ')}`
                      : ''}
                  </strong>
                </p>
              ) : null}
              <p>
                <span>Origen</span>
                <strong>
                  {pedido.origen === 'web' ? (
                    <span className="origen-pill origen-web">Tienda web</span>
                  ) : (
                    'App vendedor'
                  )}
                </strong>
              </p>
              <p>
                <span>Vendedor</span>
                <strong>
                  {pedido.origen === 'web' ? '— (pedido web)' : pedido.vendedor || '—'}
                </strong>
              </p>
              {pedido.cliente_telefono ? (
                <p>
                  <span>Teléfono</span>
                  <strong>{pedido.cliente_telefono}</strong>
                </p>
              ) : null}
              <p>
                <span>Documento</span>
                <strong>{labelTipoDoc(pedido.tipo_documento)}</strong>
              </p>
              <p>
                <span>{labelDocCampo(pedido.tipo_documento)}</span>
                <strong>{String(pedido.documento_numero || '').trim() || '—'}</strong>
              </p>
              <p>
                <span>Pago</span>
                <strong>{labelPago(pedido.forma_pago)}</strong>
              </p>
              {pedido.horario_recibo ? (
                <p>
                  <span>Horario recibo</span>
                  <strong>{pedido.horario_recibo}</strong>
                </p>
              ) : null}
              {pedido.transportadora ? (
                <p>
                  <span>Transportadora</span>
                  <strong>{pedido.transportadora}</strong>
                </p>
              ) : null}
              <p><span>Total</span><strong>{formatCOP(pedido.subtotal)}</strong></p>
              <p><span>Creado</span><strong>{fmtFecha(pedido.created_at)}</strong></p>
              {fmtDuracionVisita(pedido.visita_inicio, pedido.visita_fin) ? (
                <p>
                  <span>Demora tomando pedido</span>
                  <strong>{fmtDuracionVisita(pedido.visita_inicio, pedido.visita_fin)}</strong>
                </p>
              ) : null}
              {(() => {
                const links = visitaMapLinks(pedido);
                if (!links) return null;
                return (
                  <p className="pedido-visita-map">
                    <span>Ubicación visita</span>
                    <strong>
                      <button type="button" className="link-btn" onClick={() => setShowMapa(true)}>
                        Historial mapa
                      </button>
                      {' · '}
                      <a href={links.mapa} target="_blank" rel="noreferrer">Google</a>
                      {links.ruta ? (
                        <>
                          {' · '}
                          <a href={links.ruta} target="_blank" rel="noreferrer">Ruta</a>
                        </>
                      ) : null}
                      <em className="visita-gps-count"> {links.puntos} pts</em>
                    </strong>
                  </p>
                );
              })()}
            </div>

            <div className="sep-card">
              <h4>Bodega</h4>
              {pedido.preparado_por_nombre ? (
                <p className="sep-row">
                  <span>Separado por</span>
                  <strong>{pedido.preparado_por_nombre}</strong>
                </p>
              ) : (
                <p className="sep-row">
                  <span>Separado por</span>
                  <strong>—</strong>
                </p>
              )}
              {pedido.preparado_at ? (
                <p className="sep-row">
                  <span>Inicio separación</span>
                  <strong>{fmtFecha(pedido.preparado_at)}</strong>
                </p>
              ) : null}
              {pedido.revisado_por_nombre ? (
                <p className="sep-row">
                  <span>Revisado por</span>
                  <strong>{pedido.revisado_por_nombre}</strong>
                </p>
              ) : null}
              {pedido.revisado_at ? (
                <p className="sep-row">
                  <span>Completado</span>
                  <strong>{fmtFecha(pedido.revisado_at)}</strong>
                </p>
              ) : pedido.estado === 'entregado' && pedido.preparado_at ? (
                <p className="sep-row">
                  <span>Completado</span>
                  <strong>{fmtFecha(pedido.preparado_at)}</strong>
                </p>
              ) : null}
              {pedido.notas_bodega?.trim() ? (
                <p className="sep-row" style={{ alignItems: 'flex-start' }}>
                  <span>Notas bodega</span>
                  <strong style={{ textAlign: 'right', whiteSpace: 'pre-wrap' }}>
                    {pedido.notas_bodega}
                  </strong>
                </p>
              ) : null}
              {pedido.asignado_a_nombre ? (
                <p className="sep-row">
                  <span>Asignado a (legado)</span>
                  <strong>{pedido.asignado_a_nombre}</strong>
                </p>
              ) : null}
            </div>

            <div className="sep-card">
              <h4>Facturación</h4>
              <p className="sep-row">
                <span>Estado</span>
                <strong>{labelFactEstado(pedido.facturacion_estado)}</strong>
              </p>
              <p className="sep-row">
                <span>Documento</span>
                <strong>{labelTipoDoc(pedido.tipo_documento)}</strong>
              </p>
              <p className="sep-row">
                <span>{labelDocCampo(pedido.tipo_documento)}</span>
                <strong>{String(pedido.documento_numero || '').trim() || '—'}</strong>
              </p>
              <p className="sep-row">
                <span>Quién factura</span>
                <strong>{pedido.facturado_por_nombre || '—'}</strong>
              </p>
              <p className="sep-row">
                <span>Hora inicio</span>
                <strong>{fmtFecha(pedido.facturado_inicio_at)}</strong>
              </p>
              <p className="sep-row">
                <span>Hora fin</span>
                <strong>{fmtFecha(pedido.facturado_fin_at)}</strong>
              </p>
            </div>

            {(() => {
              const t = calcularTiempos(pedido);
              if (!t || (!t.etapas.length && !t.total)) return null;
              return (
                <div className="tiempos-card">
                  <h4>Tiempos del pedido</h4>
                  <div className="tiempos-grid">
                    {t.etapas.map((e) => (
                      <p key={e.label} className="tiempo-row">
                        <span>{e.label}</span>
                        <strong>{e.valor}</strong>
                      </p>
                    ))}
                  </div>
                  {t.total ? (
                    <p className="tiempo-total">
                      <span>{t.cancelado && !t.entregado ? 'Total hasta cancelar' : 'Total (pedido → entregado)'}</span>
                      <strong>{t.total}</strong>
                    </p>
                  ) : null}
                </div>
              );
            })()}

            {pedido.estado_log?.length ? (
              <div className="estado-log">
                <h4>Historial del pedido</h4>
                <ul>
                  {pedido.estado_log.map((ev, i) => {
                    const prev = pedido.estado_log[i - 1];
                    const delta = prev ? fmtDelta(prev.created_at, ev.created_at) : null;
                    return (
                      <li key={ev.id || `${ev.created_at}-${i}`}>
                        <div>
                          <strong>{ESTADO[ev.estado_nuevo] || ev.estado_nuevo}</strong>
                          {ev.usuario_nombre ? <span> · {ev.usuario_nombre}</span> : null}
                          {ev.usuario_rol ? <span className="log-rol"> ({ev.usuario_rol})</span> : null}
                          {ev.nota ? <em> — {ev.nota}</em> : null}
                        </div>
                        <small>
                          {fmtFecha(ev.created_at)}
                          {delta ? <span className="log-delta"> · +{delta}</span> : null}
                        </small>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <p className="muted">Aún no hay cambios de estado registrados en el historial.</p>
            )}

            {pedido.notas?.trim() ? (
              <div className="pedido-detail-notas" style={{ marginBottom: 12 }}>
                <h4>Notas del pedido</h4>
                <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{pedido.notas}</p>
              </div>
            ) : null}

            {pedido.items?.length ? (
              <div className="pedido-detail-items">
                <h4>Productos ({pedido.items.length})</h4>
                <ul>
                  {pedido.items.map((it) => {
                    const img = imageUrl(it.imagen);
                    return (
                      <li key={it.id || `${it.codigo}-${it.nombre}`}>
                        {img ? (
                          <img src={img} alt="" className="pedido-item-thumb" loading="lazy" />
                        ) : (
                          <div className="pedido-item-thumb pedido-item-thumb--empty" aria-hidden="true" />
                        )}
                        <div className="pedido-item-main">
                          <span>{it.codigo} · {it.nombre}</span>
                          {it.observaciones?.trim() ? (
                            <em className="pedido-item-obs">Obs: {it.observaciones}</em>
                          ) : null}
                        </div>
                        <strong>× {it.cantidad}</strong>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {showMapa && pedido ? (
        <VisitaMapModal
          visita={{
            ...(pedido.visita || {}),
            vendedor_nombre: pedido.vendedor || pedido.visita?.vendedor_nombre,
            sucursal_nombre: pedido.cliente_nombre || pedido.visita?.sucursal_nombre,
            inicio: pedido.visita_inicio || pedido.visita?.inicio,
            fin: pedido.visita_fin || pedido.visita?.fin,
          }}
          onClose={() => setShowMapa(false)}
        />
      ) : null}
    </div>
  );
}
